// Calendar — past & future meetings from Outlook + Fireflies, merged.
//
// Week time-grid (Mon–Sun) with month and agenda modes, plus hide-toggles
// for internal meetings and all-day blocks (statuses like WFH/travel drown
// the business meetings otherwise). Events are synced meeting
// notes served by GET /api/calendar/events; each shows its linked
// company/contact (or an amber "not linked" flag with an inline quick-link),
// the Fireflies transcript when one merged onto the calendar event, and a
// Join button for upcoming online meetings. Users see their own meetings;
// admins get a person filter (backend-enforced via view_team_calendars).
//
// Timezones: the API serializes start/end as aware-UTC ISO; the grid renders
// them in browser-local time. All-day events place by the DATE SUBSTRING of
// start (no tz conversion) — a UTC-midnight all-day event would otherwise
// shift a day west of UTC.

const CAL_HOUR_PX = 44;

const calDayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const calWeekStart = (anchor) => {
  const d = new Date(anchor);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));   // back to Monday
  return d;
};

const calFmtTime = (d) =>
  d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '');

const calDayLabel = (d) =>
  d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

// /api/calendar/events response → rows with parsed local Date + hour fields
// the grid lays out by. A missing end is treated as a one-hour meeting.
const calNormalizeEvents = (res) => (res?.events || []).map((e) => {
  const _startD = e.start ? new Date(e.start) : null;
  const _endD = e.end ? new Date(e.end) : (_startD ? new Date(_startD.getTime() + 3600e3) : null);
  return {
    ...e, _startD, _endD,
    _start: _startD ? _startD.getTime() : 0,
    _end: _endD ? _endD.getTime() : 0,
    _startH: _startD ? _startD.getHours() + _startD.getMinutes() / 60 : 9,
    _endH: _endD ? Math.max(_endD.getHours() + _endD.getMinutes() / 60,
                            (_startD?.getHours() ?? 8) + 0.5) : 10,
  };
});

// Column assignment for overlapping events within one day: sort by start,
// greedily reuse the first free column; every event in an overlap cluster
// splits the day's width by the cluster's column count.
function calLayoutDay(events) {
  const sorted = [...events].sort((a, b) => a._start - b._start || a._end - b._end);
  const colEnd = [];            // per-column current end time
  let cluster = [];             // events whose columns overlap in time
  let clusterEnd = null;
  const flush = () => {
    const n = Math.max(1, ...cluster.map((e) => e._col + 1));
    cluster.forEach((e) => { e._cols = n; });
    cluster = [];
  };
  sorted.forEach((e) => {
    if (clusterEnd !== null && e._start >= clusterEnd) { flush(); colEnd.length = 0; clusterEnd = null; }
    let col = colEnd.findIndex((end) => end <= e._start);
    if (col === -1) col = colEnd.length;
    colEnd[col] = e._end;
    e._col = col;
    cluster.push(e);
    clusterEnd = clusterEnd === null ? e._end : Math.max(clusterEnd, e._end);
  });
  flush();
  return sorted;
}

// ── Event chip (shared by grid + agenda) ─────────────────────────────────

const CalSourceDots = ({ ev }) => (
  <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center' }}>
    {ev.transcript_url && (
      <span title="Fireflies transcript" style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--violet-fg)' }} />
    )}
    {ev.has_note && (
      <span title="Note logged from this meeting" style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--green-fg)' }} />
    )}
  </span>
);

// Per-person accent for the admin "Everyone" view — a stable hash of the
// member id onto the pill palette, so a person keeps one color across the
// grid, agenda, and drawer (and across reloads).
const CAL_OWNER_HUES = ['teal', 'violet', 'green', 'rose', 'amber', 'blue'];
const calOwnerHue = (owner) => {
  const s = String(owner?.id || owner?.name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h * 31) + s.charCodeAt(i)) >>> 0;
  return CAL_OWNER_HUES[h % CAL_OWNER_HUES.length];
};

const calShortName = (name) => {
  const parts = String(name || '').trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : (parts[0] || '');
};

const CalChipColors = (ev) => {
  const c = ev.linked
    ? { background: 'var(--accent-soft)', color: 'var(--accent-text)', borderLeft: '2px solid var(--accent)' }
    : ev.internal_only
      // slate pill colors, NOT --surface-2: the page background IS
      // --surface-2, which made internal chips render as bare floating text.
      ? { background: 'var(--slate-bg)', color: 'var(--slate-fg)', borderLeft: '2px solid var(--border-strong)' }
      : { background: 'var(--amber-bg)', color: 'var(--amber-fg)', borderLeft: '2px solid var(--amber-fg)' };
  // Everyone mode: the border carries WHOSE calendar this is; the background
  // keeps carrying link status.
  if (ev.owner) c.borderLeft = `3px solid ${HUE[calOwnerHue(ev.owner)].dot}`;
  return c;
};

// ── Week grid ────────────────────────────────────────────────────────────

const CalWeekGrid = ({ days, eventsByDay, allDayByDay, onOpen }) => {
  // Auto-widen the visible hours to include every event; default 7am–7pm.
  let hStart = 7, hEnd = 19;
  Object.values(eventsByDay).forEach((list) => list.forEach((e) => {
    hStart = Math.min(hStart, Math.floor(e._startH));
    hEnd = Math.max(hEnd, Math.ceil(e._endH));
  }));
  hStart = Math.max(0, hStart); hEnd = Math.min(24, Math.max(hEnd, hStart + 1));
  const hours = []; for (let h = hStart; h < hEnd; h++) hours.push(h);
  const now = new Date();
  const todayKey = calDayKey(now);
  const hasAllDay = days.some((d) => (allDayByDay[calDayKey(d)] || []).length > 0);

  const gridRef = React.useRef(null);
  React.useEffect(() => {
    // Land the initial scroll just above the workday, not at midnight.
    const el = gridRef.current;
    if (el) el.scrollTop = Math.max(0, (8 - hStart) * CAL_HOUR_PX - 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chip = (e, style) => (
    <div
      key={e.id}
      onClick={() => onOpen(e)}
      title={`${e.title}${e.company ? ' — ' + (e.company.name || '') : ''}`}
      style={{
        ...style, ...CalChipColors(e),
        borderRadius: 4, padding: '2px 5px', fontSize: 11, lineHeight: '14px',
        cursor: 'pointer', overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{e.title}</span>
        <CalSourceDots ev={e} />
      </div>
      <div style={{ opacity: 0.85, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {e.owner ? `${calShortName(e.owner.name)} · ` : ''}
        {e.company ? e.company.name : (e.contact ? e.contact.name : (e.internal_only ? 'Internal' : 'No company · link?'))}
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Day headers */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <div style={{ width: 52, flexShrink: 0 }} />
        {days.map((d) => {
          const isToday = calDayKey(d) === todayKey;
          return (
            <div key={calDayKey(d)} style={{ flex: 1, padding: '6px 8px', borderLeft: '1px solid var(--border)', textAlign: 'left' }}>
              <span style={{ fontSize: 11, color: isToday ? 'var(--accent)' : 'var(--text-3)', fontWeight: isToday ? 700 : 500 }}>
                {d.toLocaleDateString([], { weekday: 'short' })}{' '}
                <span style={{ fontSize: 13 }}>{d.getDate()}</span>
              </span>
            </div>
          );
        })}
      </div>

      {/* All-day row */}
      {hasAllDay && (
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <div style={{ width: 52, flexShrink: 0, fontSize: 10, color: 'var(--text-4)', padding: '3px 6px' }}>all-day</div>
          {days.map((d) => (
            <div key={calDayKey(d)} style={{ flex: 1, borderLeft: '1px solid var(--border)', padding: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              {(allDayByDay[calDayKey(d)] || []).map((e) => chip(e, {}))}
            </div>
          ))}
        </div>
      )}

      {/* Hour grid */}
      <div ref={gridRef} style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        <div style={{ display: 'flex', position: 'relative', height: hours.length * CAL_HOUR_PX }}>
          <div style={{ width: 52, flexShrink: 0 }}>
            {hours.map((h) => (
              <div key={h} style={{ height: CAL_HOUR_PX, fontSize: 10, color: 'var(--text-4)', textAlign: 'right', paddingRight: 6, transform: 'translateY(-6px)' }}>
                {h === 0 ? '' : `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`}
              </div>
            ))}
          </div>
          {days.map((d) => {
            const key = calDayKey(d);
            const list = calLayoutDay(eventsByDay[key] || []);
            const isToday = key === todayKey;
            const nowTop = (now.getHours() + now.getMinutes() / 60 - hStart) * CAL_HOUR_PX;
            return (
              <div key={key} style={{ flex: 1, position: 'relative', borderLeft: '1px solid var(--border)', background: isToday ? 'color-mix(in srgb, var(--accent) 3%, transparent)' : 'transparent' }}>
                {hours.map((h) => (
                  <div key={h} style={{ height: CAL_HOUR_PX, borderBottom: '1px solid var(--border)', opacity: 0.5 }} />
                ))}
                {isToday && nowTop >= 0 && nowTop <= hours.length * CAL_HOUR_PX && (
                  <div style={{ position: 'absolute', left: 0, right: 0, top: nowTop, height: 2, background: 'var(--rose-fg)', zIndex: 3, pointerEvents: 'none' }} />
                )}
                {list.map((e) => {
                  const top = (e._startH - hStart) * CAL_HOUR_PX;
                  const height = Math.max(18, (e._endH - e._startH) * CAL_HOUR_PX - 2);
                  const w = 100 / (e._cols || 1);
                  return chip(e, {
                    position: 'absolute', top, height, zIndex: 2,
                    left: `calc(${e._col * w}% + 2px)`, width: `calc(${w}% - 5px)`,
                  });
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Month grid ───────────────────────────────────────────────────────────

const CAL_MONTH_MAX_CHIPS = 3;

const CalMonthGrid = ({ days, eventsByDay, allDayByDay, monthIdx, onOpen, onDrill }) => {
  const todayKey = calDayKey(new Date());
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>
        {days.slice(0, 7).map((d) => (
          <div key={calDayKey(d)} style={{ flex: 1, padding: '5px 8px', fontSize: 11, fontWeight: 500, color: 'var(--text-3)', borderLeft: '1px solid var(--border)' }}>
            {d.toLocaleDateString([], { weekday: 'short' })}
          </div>
        ))}
      </div>
      {weeks.map((row) => (
        <div key={calDayKey(row[0])} style={{ display: 'flex', flex: 1, minHeight: 106, borderBottom: '1px solid var(--border)' }}>
          {row.map((d) => {
            const key = calDayKey(d);
            const list = [...(allDayByDay[key] || []), ...(eventsByDay[key] || [])];
            const inMonth = d.getMonth() === monthIdx;
            const isToday = key === todayKey;
            const extra = list.length - CAL_MONTH_MAX_CHIPS;
            return (
              <div
                key={key}
                onClick={() => onDrill(d)}
                title="Open this week"
                style={{
                  flex: 1, minWidth: 0, padding: '4px 4px 6px', borderLeft: '1px solid var(--border)', cursor: 'pointer',
                  background: isToday ? 'color-mix(in srgb, var(--accent) 3%, transparent)' : (inMonth ? 'transparent' : 'var(--surface)'),
                  opacity: inMonth ? 1 : 0.55,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 500, color: isToday ? 'var(--accent)' : 'var(--text-3)', padding: '0 3px 3px' }}>{d.getDate()}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {list.slice(0, CAL_MONTH_MAX_CHIPS).map((e) => (
                    <div
                      key={e.id}
                      onClick={(ev2) => { ev2.stopPropagation(); onOpen(e); }}
                      title={`${e.is_all_day ? 'All day' : (e._startD ? calFmtTime(e._startD) : '')} · ${e.title}${e.owner ? ` (${e.owner.name})` : ''}`}
                      style={{
                        ...CalChipColors(e), borderRadius: 3, padding: '1px 4px', fontSize: 10.5, lineHeight: '14px',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {!e.is_all_day && e._startD && <span className="num" style={{ opacity: 0.8, marginRight: 3 }}>{calFmtTime(e._startD)}</span>}
                      {e.title}
                    </div>
                  ))}
                  {extra > 0 && (
                    <div style={{ fontSize: 10.5, color: 'var(--text-3)', padding: '0 3px' }}>+{extra} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// ── Agenda list ──────────────────────────────────────────────────────────

const CalAgenda = ({ days, eventsByDay, allDayByDay, onOpen }) => {
  const todayKey = calDayKey(new Date());
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px 24px' }}>
      {days.map((d) => {
        const key = calDayKey(d);
        const list = [...(allDayByDay[key] || []), ...(eventsByDay[key] || [])];
        return (
          <div key={key} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0 4px', position: 'sticky', top: 0, background: 'var(--surface-2)', zIndex: 1 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: key === todayKey ? 'var(--accent)' : 'var(--text-2)' }}>
                {calDayLabel(d)}
              </span>
              {key === todayKey && <Pill hue="blue" sz="sm">Today</Pill>}
              <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{list.length ? `${list.length} meeting${list.length === 1 ? '' : 's'}` : ''}</span>
            </div>
            {list.length === 0 ? (
              <div style={{ fontSize: 11.5, color: 'var(--text-4)', padding: '2px 0 2px 2px' }}>—</div>
            ) : list.map((e) => (
              <div
                key={e.id}
                onClick={() => onOpen(e)}
                onMouseEnter={(ev) => { ev.currentTarget.style.background = 'var(--surface-hover)'; }}
                onMouseLeave={(ev) => { ev.currentTarget.style.background = 'var(--surface)'; }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', marginBottom: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}
              >
                <span className="num" style={{ fontSize: 11.5, color: 'var(--text-3)', width: 96, flexShrink: 0 }}>
                  {e.is_all_day ? 'All day' : `${calFmtTime(e._startD)} – ${calFmtTime(e._endD)}`}
                </span>
                <span title={e.title} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.title}</span>
                <CalSourceDots ev={e} />
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  {e.owner && <Pill hue={calOwnerHue(e.owner)} sz="sm" dot>{e.owner.name}</Pill>}
                  {e.company
                    ? <Pill hue="teal" sz="sm">{e.company.name}</Pill>
                    : (e.contact
                      ? <Pill hue="teal" sz="sm">{e.contact.name}</Pill>
                      : (e.internal_only
                        ? <Pill hue="slate" sz="sm">Internal</Pill>
                        : <Pill hue="amber" sz="sm" dot>Not linked</Pill>))}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ── Event drawer: details, quick-link, add note ──────────────────────────

const CalEventDrawer = ({ ev, activeUser, onClose, onChanged, goToCompany, goToContact }) => {
  const [linkCompany, setLinkCompany] = React.useState('');
  const [linkCompanyName, setLinkCompanyName] = React.useState('');
  const [linkContact, setLinkContact] = React.useState(null);
  const [linking, setLinking] = React.useState(false);
  const [composing, setComposing] = React.useState(false);
  const [noteBody, setNoteBody] = React.useState('');
  const [noteSubtype, setNoteSubtype] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [followUp, setFollowUp] = React.useState(false);

  if (!ev) return null;
  const linkTargetId = ev.note_ids.calendar || ev.note_ids.fireflies;
  const upcoming = ev._endD && ev._endD > new Date();

  const doLink = async () => {
    if (!linkCompany && !linkContact) return;
    setLinking(true);
    try {
      // Link BOTH halves of a merged event so the record feeds agree.
      const payload = { company_id: linkCompany || linkContact?.company_id || null, contact_id: linkContact?.id || null };
      for (const nid of [ev.note_ids.calendar, ev.note_ids.fireflies].filter(Boolean)) {
        await window.AtlasApi.manuallyLinkNote(nid, payload);
      }
      window.pushToast({ type: 'success', title: 'Meeting linked' });
      window.dispatchEvent(new CustomEvent('atlas-data-changed'));
      // What it's linked to now — a host that keeps this drawer open on its
      // own copy of the event (the phone) merges it so the note saves
      // against the record. Desktop re-reads the event and ignores it.
      const contactName = linkContact ? `${linkContact.first_name || ''} ${linkContact.last_name || ''}`.trim() : '';
      onChanged({
        company: payload.company_id ? { id: payload.company_id, name: linkCompanyName || linkContact?.company_name || '' } : null,
        contact: linkContact ? { id: linkContact.id, name: contactName } : null,
      });
    } catch (e2) {
      window.pushToast({ type: 'error', title: 'Could not link meeting', body: e2?.message || null });
    } finally { setLinking(false); }
  };

  const saveNote = async () => {
    if (!noteBody.trim()) return;
    setSaving(true);
    try {
      const startD = ev._startD || new Date();
      await window.AtlasApi.createNote({
        body: noteBody.trim(),
        title: `Re: ${ev.title}`,
        note_type: 'meeting',
        dc_note_subtype: noteSubtype || null,
        call_date: calDayKey(startD),
        company_id: ev.company?.id || null,
        contact_id: ev.contact?.id || null,
        related_note_id: linkTargetId,
        author_id: activeUser?.id || null,
        author_name: activeUser?.full_name || null,
      });
      window.pushToast({ type: 'success', title: 'Note added to the record' });
      window.dispatchEvent(new CustomEvent('atlas-data-changed'));
      setComposing(false); setNoteBody(''); setNoteSubtype('');
      onChanged();
    } catch (e2) {
      window.pushToast({ type: 'error', title: 'Could not save note', body: e2?.message || null });
    } finally { setSaving(false); }
  };

  const row = (label, node) => (
    <div style={{ display: 'flex', gap: 10, padding: '5px 0', fontSize: 12.5 }}>
      <span style={{ width: 84, flexShrink: 0, color: 'var(--text-3)' }}>{label}</span>
      <span style={{ color: 'var(--text)', minWidth: 0 }}>{node}</span>
    </div>
  );

  return (
    <Drawer open onClose={onClose} title={ev.title} subtitle={ev.source === 'merged' ? 'Outlook + Fireflies' : (ev.source === 'fireflies' ? 'Fireflies' : 'Outlook')} width={520}>
      <div style={{ padding: '4px 2px' }}>
        {row('When', ev.is_all_day
          ? `${fmtDate(ev.start)} · all day`
          : `${calDayLabel(ev._startD)} · ${calFmtTime(ev._startD)} – ${calFmtTime(ev._endD)}${ev.end_is_estimate ? ' (approx.)' : ''}`)}
        {ev.location && row('Where', ev.location)}
        {ev.owner && row('Calendar', <Pill hue={calOwnerHue(ev.owner)} sz="sm" dot>{ev.owner.name}</Pill>)}
        {row('Linked to', (ev.company || ev.contact) ? (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {ev.company && (
              <a onClick={(e) => goToCompany(ev.company.id, e)} style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}>
                {ev.company.name || 'Company'}
              </a>
            )}
            {ev.contact && (
              <a onClick={(e) => goToContact(ev.contact.id, e)} style={{ color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}>
                {ev.contact.name || 'Contact'}
              </a>
            )}
            {ev.linked_via && <Pill hue="slate" sz="sm">{ev.linked_via === 'manual' ? 'linked manually' : `matched by ${ev.linked_via.replace('_', ' ')}`}</Pill>}
          </span>
        ) : (ev.internal_only
          ? <Pill hue="slate" sz="sm">Internal meeting — no outside party</Pill>
          : <Pill hue="amber" sz="sm" dot>Not linked to a record</Pill>))}

        {/* External attendees */}
        {(ev.external_attendees || []).length > 0 && row('Attendees',
          <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
            {ev.external_attendees.map((a) => <Pill key={a} hue="slate" sz="sm">{a}</Pill>)}
          </span>)}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, margin: '10px 0', flexWrap: 'wrap' }}>
          {ev.join_url && upcoming && (
            <Btn variant="primary" onClick={() => window.open(ev.join_url, '_blank', 'noopener')}>Join meeting</Btn>
          )}
          {ev.external_url && (
            <Btn onClick={() => window.open(ev.external_url, '_blank', 'noopener')}>Open in Outlook</Btn>
          )}
          {ev.transcript_url && (
            <Btn onClick={() => window.open(ev.transcript_url, '_blank', 'noopener')}>Transcript</Btn>
          )}
          <Btn active={followUp} title="Create a follow-up reminder from this meeting"
                onClick={() => setFollowUp(!followUp)}>⏰ Follow up</Btn>
        </div>

        {/* Follow-up reminder — task linked to the meeting's matched record.
            Tasks have no event FK, so the meeting rides in the notes text. */}
        {followUp && (
          <window.FollowUpComposer
            seedTitle={`Follow up: ${ev.title}`}
            links={{ company_id: ev.company?.id, contact_id: ev.contact?.id }}
            sourceLine={`From meeting "${ev.title}" (${ev.is_all_day ? fmtDate(ev.start) : calDayLabel(ev._startD)})`}
            linkedName={ev.company?.name || ev.contact?.name}
            onClose={() => setFollowUp(false)}
            style={{ marginBottom: 10 }} />
        )}

        {/* Quick-link — only when unlinked */}
        {!ev.linked && (
          <Section title="Link to a record">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Field label="Company">
                <Combobox
                  value={linkCompany}
                  // Combobox reports a change object, not the bare id.
                  onChange={(e) => { setLinkCompany(e.value); setLinkCompanyName(e.label || ''); }}
                  options={[]}
                  loadOptions={window.searchCompanyOptions}
                  placeholder="Search companies…"
                />
              </Field>
              <Field label="Contact">
                <window.ContactPicker
                  value={linkContact ? `${linkContact.first_name || ''} ${linkContact.last_name || ''}`.trim() : ''}
                  onPick={(c) => setLinkContact(c)}
                  placeholder="Search people…"
                />
              </Field>
              <div>
                <Btn variant="primary" disabled={linking || (!linkCompany && !linkContact)} onClick={doLink}>
                  {linking ? 'Linking…' : 'Link meeting'}
                </Btn>
              </div>
            </div>
          </Section>
        )}

        {/* Add note from meeting */}
        <Section title="Meeting note">
          {ev.has_note && (
            <div style={{ fontSize: 11.5, color: 'var(--green-fg)', marginBottom: 6 }}>✓ A note has been logged from this meeting.</div>
          )}
          {!composing ? (
            <Btn onClick={() => setComposing(true)} disabled={!ev.linked}
                 title={ev.linked ? '' : 'Link the meeting to a company or contact first'}>
              Add note{ev.linked ? '' : ' (link the meeting first)'}
            </Btn>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Field label="Subtype">
                <Combobox
                  value={noteSubtype}
                  onChange={(e) => setNoteSubtype(e.value)}
                  options={[{ value: '', label: '— none —' }, ...NOTE_SUBTYPES]}
                  placeholder="— none —"
                />
              </Field>
              <textarea
                value={noteBody}
                onChange={(e2) => setNoteBody(e2.target.value)}
                placeholder="Prep, debrief, next steps… lands on the record's notes feed."
                rows={5}
                style={{ width: '100%', resize: 'vertical', fontSize: 12.5, fontFamily: 'inherit', padding: 8, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text)' }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn variant="primary" disabled={saving || !noteBody.trim()} onClick={saveNote}>{saving ? 'Saving…' : 'Save note'}</Btn>
                <Btn onClick={() => setComposing(false)}>Cancel</Btn>
              </div>
            </div>
          )}
        </Section>
      </div>
    </Drawer>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────

const CalendarView = ({ activeUser, currentRole, goToCompany, goToContact }) => {
  const isAdmin = (currentRole || activeUser?.role || '').toLowerCase() === 'admin';
  const [anchor, setAnchor] = React.useState(() => new Date());
  const [mode, setMode] = React.useState(() => {             // 'week' | 'month' | 'agenda'
    const m = localStorage.getItem('atlas.calendar.mode');
    return ['week', 'month', 'agenda'].includes(m) ? m : 'week';
  });
  const [person, setPerson] = React.useState('');            // '' self · 'all' · member id
  const [people, setPeople] = React.useState([]);
  const [events, setEvents] = React.useState(null);
  const [openEvent, setOpenEvent] = React.useState(null);
  const [hideInternal, setHideInternal] = React.useState(() => localStorage.getItem('atlas.calendar.hideInternal') === '1');
  const [hideAllDay, setHideAllDay] = React.useState(() => localStorage.getItem('atlas.calendar.hideAllDay') === '1');
  const reqSeq = React.useRef(0);

  const setModeP = (m) => { setMode(m); localStorage.setItem('atlas.calendar.mode', m); };

  // Week/agenda span the anchor's Mon–Sun; month spans full weeks covering
  // the anchor's month (up to 42 days — under the API's 62-day range cap).
  const monthFirst = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const nDays = mode === 'month'
    ? Math.ceil((((monthFirst.getDay() + 6) % 7) + new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate()) / 7) * 7
    : 7;
  const start = calWeekStart(mode === 'month' ? monthFirst : anchor);
  const days = Array.from({ length: nDays }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  const end = new Date(start); end.setDate(end.getDate() + nDays);

  React.useEffect(() => {
    if (!isAdmin) return;
    window.AtlasApi.calendarPeople().then(setPeople).catch(() => setPeople([]));
  }, [isAdmin]);

  const reload = React.useCallback(async (keepOpen) => {
    const seq = ++reqSeq.current;
    if (!keepOpen) setEvents(null);
    try {
      const p = { start: start.toISOString(), end: end.toISOString() };
      if (person) p.user_id = person;
      const res = await window.AtlasApi.calendarEvents(p);
      if (seq !== reqSeq.current) return;
      const rows = calNormalizeEvents(res);
      setEvents(rows);
      if (keepOpen && openEvent) {
        setOpenEvent(rows.find((r) => r.id === openEvent.id) || null);
      }
    } catch {
      if (seq === reqSeq.current) setEvents([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start.getTime(), end.getTime(), person]);

  React.useEffect(() => { reload(false); }, [reload]);

  // Hide-toggles are client-side: the events are already fetched, the user
  // is just decluttering the view.
  const visible = (events || []).filter((e) =>
    !(hideInternal && e.internal_only) && !(hideAllDay && e.is_all_day));
  const hiddenCount = (events || []).length - visible.length;

  // Bucket by LOCAL day; all-day events by the raw date substring instead
  // (a UTC-midnight all-day event shifts a day when converted west of UTC).
  const eventsByDay = {}; const allDayByDay = {};
  visible.forEach((e) => {
    if (e.is_all_day) {
      const key = (e.start || '').slice(0, 10);
      (allDayByDay[key] = allDayByDay[key] || []).push(e);
    } else if (e._startD) {
      const key = calDayKey(e._startD);
      (eventsByDay[key] = eventsByDay[key] || []).push(e);
    }
  });

  const shiftPeriod = (n) => setAnchor((a) => {
    if (mode === 'month') return new Date(a.getFullYear(), a.getMonth() + n, 1);
    const d = new Date(a); d.setDate(d.getDate() + n * 7); return d;
  });
  const monthLabel = (mode === 'month' ? anchor : start).toLocaleDateString([], { month: 'long', year: 'numeric' });
  const count = events ? visible.length : null;
  const countScope = mode === 'month' ? 'in view' : 'this week';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-2)' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Calendar']}
        title="Calendar"
        meta={count === null ? 'Loading…' : `${count} meeting${count === 1 ? '' : 's'} ${countScope}`}
        actions={
          <>
            {isAdmin && (
              <div style={{ width: 190 }}>
                <Combobox
                  value={person}
                  onChange={(e) => setPerson(e.target.value)}
                  options={[
                    { value: '', label: 'My meetings' },
                    { value: 'all', label: 'Everyone' },
                    ...people.map((m) => ({ value: m.id, label: m.name })),
                  ]}
                  placeholder="My meetings"
                />
              </div>
            )}
            <div style={{ display: 'inline-flex', gap: 4 }}>
              {[['week', 'Week'], ['month', 'Month'], ['agenda', 'Agenda']].map(([m, label]) => (
                <Btn key={m} size="sm" active={mode === m} onClick={() => setModeP(m)}>{label}</Btn>
              ))}
            </div>
          </>
        }
      />

      {/* Week nav strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <Btn size="sm" onClick={() => shiftPeriod(-1)}>‹</Btn>
        <Btn size="sm" onClick={() => setAnchor(new Date())}>Today</Btn>
        <Btn size="sm" onClick={() => shiftPeriod(1)}>›</Btn>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{monthLabel}</span>
        {mode !== 'month' && (
          <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
            {calDayLabel(days[0])} – {calDayLabel(days[6])}
          </span>
        )}
        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 8, alignItems: 'center' }}>
          <Btn size="sm" active={hideInternal} title="Hide internal meetings (no outside party)"
               onClick={() => { setHideInternal(!hideInternal); localStorage.setItem('atlas.calendar.hideInternal', hideInternal ? '0' : '1'); }}>
            Hide internal
          </Btn>
          <Btn size="sm" active={hideAllDay} title="Hide all-day blocks (WFH, travel, personal)"
               onClick={() => { setHideAllDay(!hideAllDay); localStorage.setItem('atlas.calendar.hideAllDay', hideAllDay ? '0' : '1'); }}>
            Hide all-day
          </Btn>
          {hiddenCount > 0 && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{hiddenCount} hidden</span>}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 10, fontSize: 11, color: 'var(--text-3)', alignItems: 'center' }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--accent-soft)', border: '1px solid var(--accent)', marginRight: 4, verticalAlign: 'middle' }} />linked</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--amber-bg)', border: '1px solid var(--amber-fg)', marginRight: 4, verticalAlign: 'middle' }} />needs linking</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--slate-bg)', border: '1px solid var(--border-strong)', marginRight: 4, verticalAlign: 'middle' }} />internal</span>
          <span><span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: 3, background: 'var(--violet-fg)', marginRight: 4, verticalAlign: 'middle' }} />transcript</span>
        </span>
      </div>

      {events === null ? (
        <div style={{ padding: 24 }}><SkeletonRows rows={8} variant="generic" /></div>
      ) : events.length === 0 ? (
        <div style={{ margin: 24, padding: 32, textAlign: 'center', border: '1px dashed var(--border-strong)', borderRadius: 8, color: 'var(--text-3)', fontSize: 12.5 }}>
          No meetings {mode === 'month' ? 'this month' : 'this week'}.
        </div>
      ) : mode === 'week' ? (
        <CalWeekGrid days={days} eventsByDay={eventsByDay} allDayByDay={allDayByDay} onOpen={setOpenEvent} />
      ) : mode === 'month' ? (
        <CalMonthGrid days={days} eventsByDay={eventsByDay} allDayByDay={allDayByDay}
                      monthIdx={anchor.getMonth()} onOpen={setOpenEvent}
                      onDrill={(d) => { setAnchor(d); setModeP('week'); }} />
      ) : (
        <CalAgenda days={days} eventsByDay={eventsByDay} allDayByDay={allDayByDay} onOpen={setOpenEvent} />
      )}

      {openEvent && (
        <CalEventDrawer
          ev={openEvent}
          activeUser={activeUser}
          onClose={() => setOpenEvent(null)}
          onChanged={() => reload(true)}
          goToCompany={(id, e) => { if (!(e?.metaKey || e?.ctrlKey)) setOpenEvent(null); goToCompany(id); }}
          goToContact={(id, e) => { if (!(e?.metaKey || e?.ctrlKey)) setOpenEvent(null); goToContact(id); }}
        />
      )}
    </div>
  );
};

window.CalendarView = CalendarView;
// The phone Calendar / Today pages (src/mobile/) reuse the date helpers, the
// event normalization and the event drawer (link · note · follow up).
window.CalEventDrawer = CalEventDrawer;
window.AtlasCal = {
  calDayKey, calWeekStart, calFmtTime, calDayLabel, calOwnerHue, calShortName,
  CalChipColors, normalizeEvents: calNormalizeEvents,
};
